'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Target,
  Check,
  Sparkles,
  CheckCircle2,
  Clock,
  Send,
} from 'lucide-react';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

/* -------------------------------------------------------------------------- */
/* Hydration-safe mockup data — module-scope constants, no randomness         */
/* -------------------------------------------------------------------------- */

type SendStatus = 'queued' | 'sending' | 'delivered';

const MOCK_OUTREACH: ReadonlyArray<{
  name: string;
  email: string;
  sentDays: number | null;
  status: SendStatus;
  statusColor: string;
}> = [
  { name: 'Horizon Capital',           email: 'partners@horizoncap.com', sentDays: 2,    status: 'delivered', statusColor: 'text-green-600' },
  { name: 'Nexus Ventures',            email: 'team@nexusventures.com',  sentDays: 2,    status: 'delivered', statusColor: 'text-green-600' },
  { name: 'Nova Partners',             email: 'hello@novapartners.com',  sentDays: 1,    status: 'delivered', statusColor: 'text-green-600' },
  { name: 'Zenith Ventures',           email: 'reach@zenith.com',        sentDays: 1,    status: 'sending',   statusColor: 'text-blue-600'  },
  { name: 'Pioneer Capital',           email: 'inbox@pioneercap.com',    sentDays: null, status: 'queued',    statusColor: 'text-gray-500'  },
];

/* -------------------------------------------------------------------------- */
/* Cursor SVG — macOS pointer arrow                                           */
/* -------------------------------------------------------------------------- */

const CursorPointer: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    aria-hidden="true"
    width="20"
    height="22"
    viewBox="0 0 20 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ filter: 'drop-shadow(0 2px 4px rgba(15,23,42,0.25))' }}
  >
    <path
      d="M2 1.5L17 11L10 12.5L13 19L10 20.5L7 14L2 17V1.5Z"
      fill="white"
      stroke="#0f172a"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

/* -------------------------------------------------------------------------- */
/* Browser frame chrome                                                       */
/* -------------------------------------------------------------------------- */

const BrowserFrame: React.FC<{
  children: React.ReactNode;
  address?: string;
  className?: string;
}> = ({ children, address = 'app.sendvc.app/dashboard', className = '' }) => (
  <div
    className={`relative rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden ${className}`}
    aria-hidden="true"
    // Decorative mockup: inert keeps its buttons out of the tab order —
    // aria-hidden content must not contain focusable elements.
    inert
  >
    <div className="flex items-center gap-3 px-3 sm:px-4 py-2.5 border-b border-gray-200 bg-gray-50">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      <div className="flex-1 mx-2 sm:mx-4 px-3 py-1 rounded-md bg-white border border-gray-200 text-[10px] sm:text-xs text-gray-500 text-center font-mono truncate">
        {address}
      </div>
      <div className="hidden sm:block w-12" />
    </div>
    <div className="relative bg-white">{children}</div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Mockup 1 — Outreach Log                                                    */
/* -------------------------------------------------------------------------- */

const OutreachLogMockup: React.FC = () => {
  const { t } = useTranslation();
  const labelFor = (status: SendStatus): string => {
    if (status === 'delivered') return t('pageContent.home.dashboardShowcase.mockupLabels.delivered');
    if (status === 'sending') return t('pageContent.home.dashboardShowcase.mockupLabels.sending');
    return t('pageContent.home.dashboardShowcase.mockupLabels.queued');
  };
  const sentFor = (days: number | null): string =>
    days === null ? '—' : t('pageContent.home.dashboardShowcase.mockupLabels.sentDaysAgoShort', { count: days });

  return (
    <BrowserFrame className="ds-mockup">
      <div className="p-4 sm:p-5 text-left">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h4 className="text-sm sm:text-base font-semibold text-gray-900">
            {t('pageContent.home.dashboardShowcase.mockupLabels.outreachLog')}
          </h4>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            {t('pageContent.home.dashboardShowcase.mockupLabels.processing')}
          </span>
        </div>

        <div className="relative overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 text-[10px] sm:text-xs font-semibold text-gray-700 w-8">●</th>
                <th className="text-left py-2 px-2 text-[10px] sm:text-xs font-semibold text-gray-700">
                  {t('pageContent.home.dashboardShowcase.mockupLabels.investor')}
                </th>
                <th className="hidden sm:table-cell text-left py-2 px-2 text-[10px] sm:text-xs font-semibold text-gray-700">
                  {t('pageContent.home.dashboardShowcase.mockupLabels.sent')}
                </th>
                <th className="text-left py-2 px-2 text-[10px] sm:text-xs font-semibold text-gray-700">
                  {t('pageContent.home.dashboardShowcase.mockupLabels.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {MOCK_OUTREACH.map((row, i) => (
                <tr
                  key={row.email}
                  className={[
                    'border-b border-gray-100 transition-colors',
                    i === 1 ? 'ds-row-h2' : '',
                    i === 3 ? 'ds-row-h4' : '',
                  ].join(' ')}
                >
                  <td className="py-2 px-2">
                    {row.status === 'delivered' && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
                    {row.status === 'sending'   && <Send         className="w-3.5 h-3.5 text-blue-500" />}
                    {row.status === 'queued'    && <Clock        className="w-3.5 h-3.5 text-gray-400" />}
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <p className="text-[11px] sm:text-sm font-medium text-gray-900 leading-tight truncate max-w-[100px] sm:max-w-none">
                        {row.name}
                      </p>
                      {i < 3 && (
                        <span className="inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded bg-blue-50 shrink-0">
                          <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#0a66c2]" fill="currentColor" aria-hidden="true">
                            <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 9.67H5.67V18.3H8.34V9.67zM7 5.9a1.55 1.55 0 1 0 0 3.1 1.55 1.55 0 0 0 0-3.1zm11.33 12.4v-4.74c0-2.54-1.36-3.72-3.17-3.72-1.46 0-2.11.8-2.48 1.37v-1.18H9.67c.04.75 0 8.63 0 8.63h2.68v-4.82c0-.24.02-.48.09-.65.19-.48.63-.98 1.36-.98.96 0 1.35.73 1.35 1.8v4.65h2.86z" />
                          </svg>
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 truncate max-w-[120px] sm:max-w-[180px]">{row.email}</p>
                  </td>
                  <td className="hidden sm:table-cell py-2 px-2 text-xs text-gray-600 whitespace-nowrap">{sentFor(row.sentDays)}</td>
                  <td className={`py-2 px-2 text-[10px] sm:text-xs whitespace-nowrap ${row.statusColor}`}>{labelFor(row.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating cursor */}
      <div className="ds-cursor-clip">
        <div className="ds-cursor ds-cursor-log">
          <CursorPointer />
        </div>
      </div>
    </BrowserFrame>
  );
};

/* -------------------------------------------------------------------------- */
/* Mockup 2 — Real-time Tracking                                              */
/* -------------------------------------------------------------------------- */

const TrackingMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <BrowserFrame className="ds-mockup">
      <div className="p-4 sm:p-6 text-left">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-sm sm:text-base font-semibold text-gray-900">
              {t('pageContent.home.dashboardShowcase.mockupLabels.professionalPlan')}
            </h4>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              {t('pageContent.home.dashboardShowcase.mockupLabels.monthlyCycle')}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            {t('pageContent.home.dashboardShowcase.mockupLabels.processing')}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4 sm:mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] sm:text-xs font-medium text-gray-700">
              {t('pageContent.home.dashboardShowcase.mockupLabels.investorsContacted')}
            </span>
            <span className="text-[10px] sm:text-xs text-gray-500">
              <span className="relative inline-block w-5 text-right align-baseline">
                <span className="ds-stat-tick-a">32</span>
                <span className="absolute left-0 top-0 right-0 text-right ds-stat-tick-b">47</span>
              </span>
              {' / 100'}
            </span>
          </div>
          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full ds-progress-fill"
              style={{ transformOrigin: 'left center', width: '100%' }}
            />
          </div>
        </div>

        {/* Delivery breakdown tiles — all derived from the outreach pipeline */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 sm:px-3 py-2 sm:py-3">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-gray-500 font-medium">
              {t('pageContent.home.dashboardShowcase.mockupLabels.contacted')}
            </p>
            <p className="relative text-lg sm:text-2xl font-bold text-primary-600 leading-none mt-1">
              <span className="ds-stat-tick-a">47</span>
              <span className="absolute inset-0 ds-stat-tick-b">50</span>
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 sm:px-3 py-2 sm:py-3">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-gray-500 font-medium">
              {t('pageContent.home.dashboardShowcase.mockupLabels.queued')}
            </p>
            <p className="relative text-lg sm:text-2xl font-bold text-blue-600 leading-none mt-1">
              <span className="ds-stat-tick-a">23</span>
              <span className="absolute inset-0 ds-stat-tick-b">22</span>
            </p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 sm:px-3 py-2 sm:py-3">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-gray-500 font-medium">
              {t('pageContent.home.dashboardShowcase.mockupLabels.remaining')}
            </p>
            <p className="relative text-lg sm:text-2xl font-bold text-gray-700 leading-none mt-1">
              <span className="ds-stat-tick-a">30</span>
              <span className="absolute inset-0 ds-stat-tick-b">28</span>
            </p>
          </div>
        </div>
      </div>

      {/* Floating cursor */}
      <div className="ds-cursor-clip">
        <div className="ds-cursor ds-cursor-track">
          <CursorPointer />
        </div>
      </div>
    </BrowserFrame>
  );
};

/* -------------------------------------------------------------------------- */
/* Mockup 3 — Sent Mail Detail                                                */
/* -------------------------------------------------------------------------- */

const SentMailMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <BrowserFrame address="app.sendvc.app/dashboard/mail" className="ds-mockup">
      <div className="p-4 sm:p-5 text-left">
        <div className="flex items-start gap-3 pb-3 border-b border-gray-100 relative">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            JD
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">Jane Doe · Apex Ventures</p>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate">jane@apexventures.com</p>
          </div>
          <span className="text-[10px] sm:text-xs text-gray-400 whitespace-nowrap">
            {t('pageContent.home.dashboardShowcase.mockupLabels.sentDaysAgoShort', { count: 2 })}
          </span>

          {/* Tooltip that pops when cursor hovers the recipient */}
          <div className="ds-tooltip absolute left-12 top-12 sm:left-14 sm:top-14 z-10 rounded-lg bg-gray-900 text-white text-[10px] sm:text-xs px-3 py-2 shadow-xl max-w-[220px]">
            <p className="font-semibold mb-0.5">Apex Ventures</p>
            <p className="text-gray-300">Series A · €15–25M check · Stockholm</p>
            <p className="text-gray-300">6 portfolio matches</p>
            <span className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 rotate-45" />
          </div>
        </div>

        <div className="py-3 border-b border-gray-100">
          <p className="text-[11px] sm:text-xs font-medium text-gray-500 mb-1">
            {t('pageContent.home.dashboardShowcase.mockupLabels.subject')}
          </p>
          <p className="text-xs sm:text-sm font-semibold text-gray-900">
            CloudSecure — looking for Seed/Series A partners
          </p>
        </div>

        <div className="py-3 text-[11px] sm:text-xs text-gray-600 leading-relaxed space-y-1.5">
          <p>Hi Jane,</p>
          <p>
            Given Apex Ventures&apos;s recent investments in security infrastructure (TechShield, DataStream), I
            wanted to share what we&apos;ve built at <span className="font-medium text-gray-900">CloudSecure</span>…
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
          <span className="ds-chip-pulse inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100">
            <Sparkles className="w-3 h-3" />
            {t('pageContent.home.dashboardShowcase.mockupLabels.personalisedFor', { name: 'Apex Ventures' })}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-gray-500">
            <Send className="w-3 h-3" />
            {t('pageContent.home.dashboardShowcase.mockupLabels.sentDaysAgo', { count: 2 })}
          </span>
        </div>
      </div>

      {/* Floating cursor */}
      <div className="ds-cursor-clip">
        <div className="ds-cursor ds-cursor-mail">
          <CursorPointer />
        </div>
      </div>
    </BrowserFrame>
  );
};

/* -------------------------------------------------------------------------- */
/* Mockup 4 — Approval Workflow                                               */
/* -------------------------------------------------------------------------- */

const ApprovalWorkflowMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <BrowserFrame className="ds-mockup">
      <div className="p-4 sm:p-5 text-left">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm sm:text-base font-semibold text-gray-900">
              {t('pageContent.home.dashboardShowcase.mockupLabels.actionNeeded')}
            </h4>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs font-bold">
              2
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {[
            { name: 'Vanguard Investments', email: 'hello@vanguardinv.com', },
            { name: 'Velocity Partners', email: 'partners@velocity.com', },
          ].map((row, i) => (
            <div key={row.email} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border border-gray-100 bg-white shadow-sm hover:border-gray-200 transition-colors ${i === 0 ? 'ds-row-h2' : ''}`}>
              <div>
                <p className="text-[11px] sm:text-sm font-medium text-gray-900 leading-tight">
                  {row.name}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{row.email}</p>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button className="px-3 py-1.5 rounded-md text-[10px] sm:text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors" type="button">
                  {t('pageContent.home.dashboardShowcase.mockupLabels.edit')}
                </button>
                <button className="px-3 py-1.5 rounded-md text-[10px] sm:text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors" type="button">
                  {t('pageContent.home.dashboardShowcase.mockupLabels.approve')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating cursor */}
      <div className="ds-cursor-clip">
        <div className="ds-cursor ds-cursor-action">
          <CursorPointer />
        </div>
      </div>
    </BrowserFrame>
  );
};

/* -------------------------------------------------------------------------- */
/* Feature row helper                                                         */
/* -------------------------------------------------------------------------- */

interface FeatureRowProps {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  mockup: React.ReactNode;
  reversed?: boolean;
  delay?: number;
}

const FeatureRow: React.FC<FeatureRowProps> = ({
  eyebrow,
  title,
  description,
  bullets,
  mockup,
  reversed = false,
  delay = 0,
}) => {
  const { ref, isVisible } = useIntersectionObserver({ threshold: 0.15 });
  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center fade-in-section ${
        isVisible ? 'is-visible' : ''
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className={reversed ? 'lg:order-2' : 'lg:order-1'}>
        <span className="inline-block bg-primary-50 rounded-full px-3 py-1 text-primary-600 text-xs font-medium uppercase tracking-wide mb-3">
          {eyebrow}
        </span>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">{title}</h3>
        <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">{description}</p>
        <ul className="space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-primary-50 flex items-center justify-center">
                <Check className="w-3 h-3 text-primary-600" strokeWidth={3} aria-hidden="true" />
              </span>
              <span className="text-sm md:text-base text-gray-700">{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={reversed ? 'lg:order-1' : 'lg:order-2'}>
        <div className="ds-mockup-wrap">{mockup}</div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Supporting card                                                            */
/* -------------------------------------------------------------------------- */

interface SupportingCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  delay?: number;
}

const SupportingCard: React.FC<SupportingCardProps> = ({ icon: Icon, title, description, delay = 0 }) => {
  const { ref, isVisible } = useIntersectionObserver({ threshold: 0.2 });
  return (
    <div
      ref={ref}
      className={`bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover-lift fade-in-section ${
        isVisible ? 'is-visible' : ''
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {/* Decorative: the icon only restates the card title next to it */}
      <div className="rounded-xl bg-primary-50 w-12 h-12 flex items-center justify-center mb-5" aria-hidden="true">
        <Icon className="w-6 h-6 text-primary-500" />
      </div>
      {/* h3, not h4: these cards are siblings of the FeatureRow <h3>s, not nested
          under them, so their nearest ancestor heading is the section <h2>. */}
      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm md:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main section                                                               */
/* -------------------------------------------------------------------------- */

const DashboardShowcase: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useIntersectionObserver();

  const rows: Array<{
    key: 'outreachLog' | 'tracking' | 'sentMail' | 'approvalWorkflow';
    mockup: React.ReactNode;
  }> = [
    { key: 'outreachLog', mockup: <OutreachLogMockup /> },
    { key: 'tracking',    mockup: <TrackingMockup /> },
    { key: 'sentMail',    mockup: <SentMailMockup /> },
    { key: 'approvalWorkflow', mockup: <ApprovalWorkflowMockup /> },
  ];

  return (
    <section id="dashboard-showcase" className="py-16 md:py-24 bg-white">
      <div className="container">
        {/* Heading */}
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-14 md:mb-20 fade-in-section ${
            isVisible ? 'is-visible' : ''
          }`}
        >
          <div className="inline-block bg-primary-50 rounded-full px-4 py-1 text-primary-600 font-medium text-sm mb-4">
            {t('pageContent.home.dashboardShowcase.tagline')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('pageContent.home.dashboardShowcase.title')}
          </h2>
          <p className="text-lg md:text-xl text-gray-600">
            {t('pageContent.home.dashboardShowcase.subtitle')}
          </p>
        </div>

        {/* Three alternating feature rows */}
        <div className="space-y-20 md:space-y-28">
          {rows.map((row, i) => {
            const bulletsRaw = t(`pageContent.home.dashboardShowcase.rows.${row.key}.bullets`, {
              returnObjects: true,
            });
            const bullets = Array.isArray(bulletsRaw)
              ? (bulletsRaw as string[])
              : [];
            return (
              <FeatureRow
                key={row.key}
                eyebrow={t(`pageContent.home.dashboardShowcase.rows.${row.key}.eyebrow`)}
                title={t(`pageContent.home.dashboardShowcase.rows.${row.key}.title`)}
                description={t(`pageContent.home.dashboardShowcase.rows.${row.key}.description`)}
                bullets={bullets}
                mockup={row.mockup}
                reversed={i % 2 === 1}
              />
            );
          })}
        </div>

        {/* Two supporting cards */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          <SupportingCard
            icon={AlertCircle}
            title={t('pageContent.home.dashboardShowcase.themes.challenges.title')}
            description={t('pageContent.home.dashboardShowcase.themes.challenges.description')}
            delay={0}
          />
          <SupportingCard
            icon={Target}
            title={t('pageContent.home.dashboardShowcase.themes.strategy.title')}
            description={t('pageContent.home.dashboardShowcase.themes.strategy.description')}
            delay={0.1}
          />
        </div>
      </div>
    </section>
  );
};

export { DashboardShowcase };
